import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { SuiteEyebrow } from "./SuiteEyebrow";

/**
 * SuiteCard — contenitore standard della Suite con header opzionale.
 *
 * Props:
 *  - eyebrow    : testo eyebrow (uppercase tracking-widest) — opzionale
 *  - title      : titolo grande della card (h3)
 *  - subtitle   : sottotitolo piccolo
 *  - eyebrowIcon: icona Lucide accanto all'eyebrow
 *  - eyebrowTone: tono dell'eyebrow (primary/secondary/accent/warning/error/neutral)
 *  - actions    : nodo React (bottoni, filtri) allineato a destra dell'header
 *  - padding    : "none" | "sm" | "md" (default) | "lg" — padding del corpo
 *  - noDivider  : rimuove il divider tra header e corpo
 *  - className  : classi extra sulla Card
 *  - children   : contenuto
 *  - testid     : data-testid
 */
export function SuiteCard({
  eyebrow, title, subtitle, eyebrowIcon, eyebrowTone = "primary",
  actions, padding = "md", noDivider = false, className = "", testid, children,
}) {
  const padMap = { none: "p-0", sm: "p-3", md: "p-5", lg: "p-6" };
  const bodyPad = padMap[padding] ?? padMap.md;
  const hasHeader = eyebrow || title || subtitle || actions;

  return (
    <Card
      className={`border-suite-neutral-200 rounded-md bg-suite-surface ${className}`}
      data-testid={testid}
    >
      {hasHeader && (
        <div className={`px-5 py-4 flex items-start justify-between gap-3 ${noDivider ? "" : "border-b border-suite-neutral-200"}`}>
          <div className="min-w-0">
            {eyebrow && (
              <SuiteEyebrow tone={eyebrowTone} icon={eyebrowIcon}>{eyebrow}</SuiteEyebrow>
            )}
            {title && (
              <div className="text-sm text-suite-neutral-800 font-semibold mt-0.5" style={{ fontFamily: "Manrope" }}>
                {title}
              </div>
            )}
            {subtitle && <div className="text-xs text-suite-neutral-500 mt-0.5">{subtitle}</div>}
          </div>
          {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
        </div>
      )}
      <CardContent className={bodyPad}>{children}</CardContent>
    </Card>
  );
}

export default SuiteCard;
